import { useState, useEffect, useRef, useCallback } from 'react';

// Custom hook for speech recognition functionality
const useSpeechRecognition = () => {
    const [transcript, setTranscript] = useState('');
    const [isListening, setIsListening] = useState(false);
    const [isSpeechRecognitionSupported, setIsSpeechRecognitionSupported] = useState(false);
    const recognitionRef = useRef(null);

    useEffect(() => {
        // Check if speech recognition is supported
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (SpeechRecognition) {
            setIsSpeechRecognitionSupported(true);
            recognitionRef.current = new SpeechRecognition();

            const recognition = recognitionRef.current;
            recognition.continuous = false;
            recognition.interimResults = false;
            recognition.lang = 'en-US';

            recognition.onresult = (event) => {
                let interimTranscript = '';
                let finalTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    } else {
                        interimTranscript += event.results[i][0].transcript;
                    }
                }
                setTranscript(finalTranscript || interimTranscript);
            };

            recognition.onstart = () => {
                setIsListening(true);
            };

            recognition.onend = () => {
                setIsListening(false);
            };

            recognition.onerror = (event) => {
                setIsListening(false);

                // Handle specific error types
                switch (event.error) {
                    case 'not-allowed':
                        setIsSpeechRecognitionSupported(false);
                        console.warn('Microphone access was denied');
                        break;
                    case 'no-speech':
                        console.warn('No speech was detected');
                        break;
                    case 'audio-capture':
                        setIsSpeechRecognitionSupported(false);
                        console.warn('No microphone was found');
                        break;
                    case 'network':
                        console.warn('Network error occurred');
                        break;
                    default:
                        console.warn('Speech recognition error occurred');
                }
            };
        } else {
            console.warn('Speech recognition not supported in this browser');
        }

        // Cleanup function
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const startListening = useCallback(async () => {
        if (!isSpeechRecognitionSupported || !recognitionRef.current) {
            console.warn('Speech recognition is not supported');
            return;
        }

        try {
            if (!isListening) {
                setTranscript(''); // Clear previous transcript
                recognitionRef.current.start();
            }
        } catch (error) {
            // Handle permission errors
            if (error.name === 'NotAllowedError') {
                console.warn('Microphone access was denied');
            }
            else if (error.name === 'NotFoundError') {
                console.warn('No microphone was found');
            }
            else {
                console.warn('Unable to access microphone');
            }
        }
    }, [isSpeechRecognitionSupported, isListening]);

    const stopListening = useCallback(() => {
        if (recognitionRef.current && isListening) {
            recognitionRef.current.stop();
        }
    }, [isListening]);

    return {
        transcript,
        isListening,
        isSpeechRecognitionSupported,
        startListening,
        stopListening,
        setTranscript
    };
};

export {
    useSpeechRecognition
};